import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSession } from '../../context/session-context';
import supabase from '../../supabase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const SignInPage = () => {
  const { session } = useSession();
  const [status, setStatus] = useState('');
  const [formValues, setFormValues] = useState({
    email: '',
    password: '',
  });

  if (session) return <Navigate to="/organization" />;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('Logging in...');

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: formValues.email,
      password: formValues.password,
    });

    if (error) {
      setStatus(`Error: ${error.message}`);
      return;
    }

    if (authData.user) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('status')
        .eq('auth_id', authData.user.id)
        .single();

      if (userError) {
        setStatus(`Error: ${userError.message}`);
        await supabase.auth.signOut();
        return;
      }

      if (userData.status !== 'active') {
        setStatus(
          'Error: Your account is inactive. Please contact your administrator.',
        );
        await supabase.auth.signOut();
        return;
      }
    }

    setStatus('');
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-bg-main">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] px-8">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Sign In</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-title font-medium">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleInputChange}
              placeholder="you@example.com"
              required
              className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
              style={{ fontSize: '16px' }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password" className="text-title font-medium">
                Password
              </Label>
              <Link
                to="/forgot-password"
                className="text-sm text-accent-purple hover:text-accent-purple/80"
              >
                Forgot?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              value={formValues.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
              style={{ fontSize: '16px' }}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
            disabled={status && !status.includes('Error') ? true : false}
          >
            {status && !status.includes('Error') ? status : 'Sign In'}
          </Button>

          {status && status.includes('Error') && (
            <div className="p-3 text-sm text-center text-destructive">
              {status.replace('Error: ', '')}
            </div>
          )}
        </form>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">Don't have an account? </span>
          <Link
            to="/register"
            className="text-accent-purple hover:text-accent-purple/80"
          >
            Register
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
